'use client';

import React from 'react';
import styled from 'styled-components';

// ============================================================================
// Types
// ============================================================================

export interface VitalsData {
  pageLabel: string;
  ttfb: number;
  fcp: number;
  lcp: number;
  tbt: number;
  cls: number;
  cls_screenshot: string;
  fcp_screenshot: string;
  lcp_screenshot: string;
  tbt_screenshot: string;
  ttfb_screenshot: string;
}

export interface VitalsTableProps {
  title?: string;
  subtitle?: string;
  data: VitalsData[];
}

// ============================================================================
// Styled Components
// ============================================================================

const TableContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dfdfdf;
`;

const TableHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const TableTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f5f5f5;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 16px 24px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:first-child {
    font-size: 14px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.15s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const TableCell = styled.td`
  padding: 16px 24px;
  font-size: 14px;
  color: #111827;
  
  &:first-child {
    font-weight: 500;
  }
`;

const MetricValue = styled.span<{ $status: 'good' | 'caution' | 'critical' }>`
  font-weight: 500;
  color: ${({ $status }) => {
    switch ($status) {
      case 'good': return '#10b981';
      case 'caution': return '#f59e0b';
      case 'critical': return '#ef4444';
    }
  }};
`;

// ============================================================================
// Helper Functions
// ============================================================================

const getStatus = (value: number, metric: string): 'good' | 'caution' | 'critical' => {
  switch (metric) {
    case 'ttfb':
      if (value <= 0.8) return 'good';
      if (value <= 1.8) return 'caution';
      return 'critical';
    case 'fcp':
      if (value <= 1.8) return 'good';
      if (value <= 3.0) return 'caution';
      return 'critical';
    case 'lcp':
      if (value <= 2.5) return 'good';
      if (value <= 4.0) return 'caution';
      return 'critical';
    case 'jsTbt':
      if (value <= 200) return 'good';
      if (value <= 600) return 'caution';
      return 'critical';
    case 'cls':
      if (value <= 0.1) return 'good';
      if (value <= 0.25) return 'caution';
      return 'critical';
    default:
      return 'good';
  }
};

const formatValue = (value: number, metric: string): string => {
  switch (metric) {
    case 'ttfb':
    case 'fcp':
    case 'lcp':
      return `${value.toFixed(2)}s`;
    case 'jsTbt':
      return `${Math.round(value)}ms`;
    case 'cls':
      return value.toFixed(2);
    default:
      return value.toString();
  }
};

// ============================================================================
// Component
// ============================================================================

/**
 * VitalsTable Component
 * 
 * Displays Core Web Vitals performance metrics in a table format.
 * Shows metrics for multiple pages with color-coded status indicators.
 * 
 * @component
 * @example
 * ```tsx
 * const data: VitalsData[] = [
 *   {
 *     pageLabel: 'Home Page',
 *     ttfb: 0.78,
 *     fcp: 2.04,
 *     lcp: 24.01,
 *     jsTbt: 2501,
 *     cls: 0.76
 *   }
 * ];
 * 
 * <VitalsTable
 *   title="Vitals by Page Label"
 *   subtitle="The Web Vitals performance of each page in your site, identify which pages your site is slow and why"
 *   data={data}
 * />
 * ```
 */
export const VitalsTable: React.FC<VitalsTableProps> = ({
  title = 'Vitals by Page Label',
  subtitle = 'The Web Vitals performance of each page in your site, identify which pages your site is slow and why',
  data
}) => {
  return (
    <TableContainer>
      {(title || subtitle) && (
        <TableHeader>
          {title && <TableTitle>{title}</TableTitle>}
          {subtitle && <TableSubtitle>{subtitle}</TableSubtitle>}
        </TableHeader>
      )}
      
      <Table>
        <TableHead>
          <TableHeaderRow>
            <TableHeaderCell>Page Domain</TableHeaderCell>
            <TableHeaderCell>TTFB</TableHeaderCell>
            <TableHeaderCell>FCP</TableHeaderCell>
            <TableHeaderCell>LCP</TableHeaderCell>
            <TableHeaderCell>JS TBT</TableHeaderCell>
            <TableHeaderCell>CLS</TableHeaderCell>
          </TableHeaderRow>
        </TableHead>
        
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.pageLabel}</TableCell>
              <TableCell>
                <MetricValue $status={getStatus(row.ttfb, 'ttfb')}>
                  {formatValue(row.ttfb, 'ttfb')}
                </MetricValue>
              </TableCell>
              <TableCell>
                <MetricValue $status={getStatus(row.fcp, 'fcp')}>
                  {formatValue(row.fcp, 'fcp')}
                </MetricValue>
              </TableCell>
              <TableCell>
                <MetricValue $status={getStatus(row.lcp, 'lcp')}>
                  {formatValue(row.lcp, 'lcp')}
                </MetricValue>
              </TableCell>
              <TableCell>
                <MetricValue $status={getStatus(row.tbt, 'tbt')}>
                  {formatValue(row.tbt, 'tbt')}
                </MetricValue>
              </TableCell>
              <TableCell>
                <MetricValue $status={getStatus(row.cls, 'cls')}>
                  {formatValue(row.cls, 'cls')}
                </MetricValue>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {data?.length === 0 && (
        <div style={{ 
          padding: '48px 24px', 
          textAlign: 'center', 
          color: '#6b7280',
          fontSize: '14px'
        }}>
          No data available
        </div>
      )}
    </TableContainer>
  );
};

export default VitalsTable;

